import { useSelector } from "react-redux";
import {
  useGetShopBasicDataQuery,
  useGetShopListDataQuery,
  useUpdateShopTableHeadDataMutation,
} from "../../../store/queries/Master";
import { updateConfig as globalUpdateConfig } from "../../../store/slices/Global";
import { updateConfig } from "../../../store/slices/Master/Shop/shopSlice";
import { useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import {
  getShopData,
  deleteShop,
  approveShopRequest,
  exportShop,
} from "./api";
import moment from "moment";
import { Button } from "@wac-ui-dashboard/wac_component_library";
import getFilterCount from "../../../utils/getFilterCount";

const useShop = () => {
  let paginationOptions = [
    { label: "20 Per page", value: 20 },
    { label: "40 Per page", value: 40 },
    { label: "60 Per page", value: 60 },
  ];

  const dispatch = useDispatch();

  const { tableFields, showEditModal } = useSelector((state) => state.global);
  const queryState = useSelector((state) => state.shop);
  const [isExporting, setIsExporting] = useState(false);
  const toFilterId = (value) =>
    value === "" || value === null || value === undefined ? "" : Number(value);
  const customerTypeId = toFilterId(queryState.filter?.customer_type);
  const addAsId = toFilterId(queryState.filter?.add_as);
  const {
    data: queryList = {},
    isLoading,
    isFetching,
    refetch,
  } = useGetShopListDataQuery({
    per_page: queryState.currentPageSize,
    page: queryState.currentPage,
    filter: queryState.currentFilter,
    sort_by: queryState.sortBy,
    sort_order: queryState.sortOrder || "desc",
    start: queryState.startDate,
    end: queryState.endDate,
    search: queryState.search,
    customer_type: customerTypeId,
    add_as: addAsId,
  });
  const { data: basicData = {} } = useGetShopBasicDataQuery();
  const [updateShopFields] = useUpdateShopTableHeadDataMutation();

  useEffect(() => {
    dispatch(
      globalUpdateConfig((state) => {
        state.tableFields = queryList?.data?.fields;
      }),
    );
    // eslint-disable-next-line
  }, [queryList]);

  useEffect(() => {
    if (queryState?.selectedShop && queryState?.isEdit) {
      fillBranchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryState?.isEdit, queryState?.showAddShopModal]);
  useEffect(() => {
    if (queryState.clearSelection) {
      dispatch(
        updateConfig((state) => {
          state.clearSelection = false;
        }),
      );
    }
    // eslint-disable-next-line
  }, [queryState.clearSelection]);

  const handleModalClick = () => {
    dispatch(
      updateConfig((state) => {
        state.showAddShopModal = !state.showAddShopModal;
        state.isEdit = false;
        state.activeTab = "Basic";
      }),
    );
  };

  const getRow = (feild, data) => {
    const rows = {
      common: (feild, data) => <p className="pro-mb-0">{data[feild]}</p>,
      customer_type: (field, data) => (
        <p className="pro-mb-0">
          {basicData?.data?.customer_type?.find(
            (item) => item?.id === data?.[field],
          )?.name ?? ""}
        </p>
      ),
      status: (field, data) =>
        data?.status === "Pending" && queryList?.data?.approve_permission ? (
          <Button
            className={"pro-btn-primary sm"}
            type="button"
            onClick={() => handleApprovePermission(data?._id)}
          >
            {"Approve"}
          </Button>
        ) : data?.status === "Deleted" ? (
          <span className={"pro-badge badge-danger-outline"}>{"Deleted"}</span>
        ) : data?.status === "Active" ? (
          <span className={"pro-badge badge-success-outline"}>{"Active"}</span>
        ) : (
          data?.status
        ),
    };

    return rows?.[feild]?.(feild, data) ?? rows["common"](feild, data);
  };
  const handleApprovePermission = (approveId) => {
    let params = {
      supplier_shop_id: approveId,
    };
    approveShopRequest(params).then((response) => {
      if (response?.data?.status_code === 200) {
        refetch();
        toast.success("Approved successfully");
      } else {
        toast.error("Something went wrong");
      }
    });
  };
  const handleDelete = () => {
    let data = { supplier_shop_id: queryState?.selectedShop };
    deleteShop(data).then((response) => {
      if (response?.data?.status_code === 200) {
        toast.success("Deleted successfully");
        handleDeleteModal();
        refetch();
      } else {
        toast.error("Something went wrong");
        handleDeleteModal();
      }
    });
  };

  const fillBranchData = () => {
    getShopData(queryState?.selectedShop).then((response) => {
      if (response?.data?.status_code === 200) {
        dispatch(
          updateConfig((state) => (state.shopData = response?.data?.data)),
        );
      } else {
        toast.error("Something went wrong to fetch data");
      }
    });
  };

  const handleDeleteModal = (e) => {
    dispatch(
      updateConfig((state) => {
        state.showDeleteModal = !state.showDeleteModal;
        state.selectedShop = e?.[0];
      }),
    );
  };

  const handleEditAction = (e) => {
    dispatch(
      updateConfig((state) => {
        state.showAddShopModal = true;
        state.selectedShop = e?.[0];
        state.isEdit = true;
        state.activeTab = "Basic";
      }),
    );
  };

  const handleSearch = (e) => {
    dispatch(
      updateConfig((state) => {
        state.search = e.target.value;
        state.currentPage = 1;
      }),
    );
  };

  const handleEditColumnsClick = () => {
    dispatch(
      globalUpdateConfig((state) => {
        state.showEditModal = !state.showEditModal;
      }),
    );
  };

  const handleClearClick = () => {
    dispatch(
      updateConfig((state) => {
        state.search = "";
        state.currentPage = 1;
        state.filter.customer_type = "";
        state.filter.add_as = "";
      }),
    );
  };

  const handleExport = () => {
    const payload = {
      filter: queryState.currentFilter,
      sort_by: queryState.sortBy,
      sort_order: queryState.sortOrder || "desc",
      start: queryState.startDate,
      end: queryState.endDate,
      search: queryState.search,
      customer_type: customerTypeId,
      add_as: addAsId,
    };
    setIsExporting(true);
    exportShop(payload)
      .then((response) => {
        setIsExporting(false);
        if (response?.status === 200) {
          const dataType = response?.data?.type;
          const downloadLink = document.createElement("a");
          downloadLink.href = window.URL.createObjectURL(
            new Blob([response?.data], { type: dataType }),
          );
          downloadLink.setAttribute(
            "download",
            `Shop_${moment(Date.now()).format("DD_MM_YYYY_HH_mm_ss")}.xlsx`,
          );
          document.body.appendChild(downloadLink);
          downloadLink.click();
          document.body.removeChild(downloadLink);
          toast.success("Exported successfully");
        } else {
          toast.error("Failed to export");
        }
      })
      .catch(() => {
        setIsExporting(false);
        toast.error("Failed to export");
      });
  };

  const handleFilterClick = () => {
    dispatch(
      updateConfig((state) => {
        state.showFilter = !state.showFilter;
      }),
    );
  };

  const filterCount = useMemo(() => {
    return getFilterCount({
      customer_type: queryState?.filter?.customer_type,
      add_as: queryState?.filter?.add_as,
    });
  }, [queryState?.filter?.customer_type, queryState?.filter?.add_as]);

  const handlePageSize = ({ value: page_size }) => {
    dispatch(
      updateConfig((state) => {
        state.currentPageSize = page_size;
        state.currentPage = 1;
      }),
    );
  };
  const handlePagination = (page) => {
    dispatch(
      updateConfig((state) => {
        state.currentPage = page;
      }),
    );
  };

  const handleSort = (label) => {
    if (queryState.sortBy === label) {
      dispatch(
        updateConfig((state) => {
          state.currentPage = 1;
          state.sortOrder = queryState.sortOrder === "asc" ? "desc" : "asc";
        }),
      );
    } else {
      dispatch(
        updateConfig((state) => {
          state.currentPage = 1;
          state.sortBy = label;
          state.sortOrder = "asc";
        }),
      );
    }
  };
  return {
    queryState,
    showEditModal,
    tableFields,
    queryList,
    isLoading,
    isFetching,
    paginationOptions,
    filterCount,
    isExporting,
    getRow,
    handleEditColumnsClick,
    handleSort,
    handlePagination,
    handleModalClick,
    handleDelete,
    handleDeleteModal,
    handleEditAction,
    handleSearch,
    updateShopFields,
    handleClearClick,
    handlePageSize,
    handleFilterClick,
    handleExport,
    refetch,
  };
};

export default useShop;
